import type { FilterClearingInfo } from "@/lib/filter-display-utils";
import {
  type FilterGroup,
  isFilterGroup,
  type StructuredFilter,
} from "@/lib/filters";
import {
  FILTER_ENTITY_TYPES,
  type FilterEntityType,
} from "@/lib/filters/types";

/**
 * Extract structured filters from filter groups (recursively handles nested groups)
 */
export function extractStructuredFilters(
  filterGroup: FilterGroup,
): StructuredFilter[] {
  const structuredFilters: StructuredFilter[] = [];

  for (const filter of filterGroup.filters) {
    if (isFilterGroup(filter)) {
      // Recursively extract from nested filter groups
      structuredFilters.push(...extractStructuredFilters(filter));
    } else {
      structuredFilters.push(filter);
    }
  }

  return structuredFilters;
}

/**
 * Rebuild FilterGroup from StructuredFilters
 */
export function rebuildFilterGroup(
  structuredFilters: StructuredFilter[],
  originalGroup: FilterGroup,
): FilterGroup {
  if (structuredFilters.length === 0) {
    return { operator: "AND", filters: [] };
  }

  // Preserve the original operator when rebuilding
  return {
    operator: originalGroup.operator,
    filters: structuredFilters,
  };
}

/**
 * Create filter clear handler for metric cards
 */
export function createFilterClearHandler(
  accountFilters: FilterGroup,
  opportunityFilters: FilterGroup,
  touchpointFilters: FilterGroup,
  setAccountFilters: (filters: FilterGroup) => void,
  setOpportunityFilters: (filters: FilterGroup) => void,
  setTouchpointFilters: (filters: FilterGroup) => void,
  campaignFilters?: FilterGroup,
  setCampaignFilters?: (filters: FilterGroup) => void,
) {
  // Create a mapping of filter types to their corresponding filters and setters
  const filterTypeMap: Record<
    string,
    { filters: FilterGroup; setter: (filters: FilterGroup) => void } | null
  > = {
    [FILTER_ENTITY_TYPES.ACCOUNT]: {
      filters: accountFilters,
      setter: setAccountFilters,
    },
    [FILTER_ENTITY_TYPES.OPPORTUNITY]: {
      filters: opportunityFilters,
      setter: setOpportunityFilters,
    },
    [FILTER_ENTITY_TYPES.TOUCHPOINT]: {
      filters: touchpointFilters,
      setter: setTouchpointFilters,
    },
  };

  // Add campaign filters if provided
  if (campaignFilters && setCampaignFilters) {
    filterTypeMap[FILTER_ENTITY_TYPES.CAMPAIGN] = {
      filters: campaignFilters,
      setter: setCampaignFilters,
    };
  }

  return (key: string, clearingInfo?: FilterClearingInfo) => {
    // Helper function to get the appropriate filters and setter
    const getFiltersAndSetter = (filterGroupType: FilterEntityType) => {
      return filterTypeMap[filterGroupType] || null;
    };

    // Use the structured clearingInfo if available (preferred approach)
    if (clearingInfo) {
      const config = getFiltersAndSetter(clearingInfo.filterGroupType);
      if (!config) {
        console.warn(
          `Unknown filter group type: ${clearingInfo.filterGroupType}`,
        );
        return;
      }

      const { filters, setter } = config;
      const structuredFilters = extractStructuredFilters(filters);

      if (clearingInfo.type === "consolidated_date_range") {
        // Handle consolidated date range filters
        const newFilters = structuredFilters.filter(
          (filter) =>
            !(
              filter.field === clearingInfo.fieldName &&
              (filter.op === "GTE" || filter.op === "LTE")
            ),
        );

        if (newFilters.length !== structuredFilters.length) {
          const newFilterGroup = rebuildFilterGroup(newFilters, filters);
          setter(newFilterGroup);
        }
      } else if (clearingInfo.type === "individual") {
        // Handle individual filters
        if (
          clearingInfo.index !== undefined &&
          structuredFilters.length > clearingInfo.index
        ) {
          const newFilters = structuredFilters.filter(
            (_, i) => i !== clearingInfo.index,
          );
          const newFilterGroup = rebuildFilterGroup(newFilters, filters);
          setter(newFilterGroup);
        } else if (clearingInfo.index === undefined) {
          console.warn(
            `Individual filter clearing requires index, but index is undefined for filterGroupType: ${clearingInfo.filterGroupType}`,
          );
        }
      }
      return;
    }

    // Fallback for backward compatibility (legacy string parsing)
    console.warn(
      `Filter cleared using legacy string parsing for key: ${key}. Consider updating to use clearingInfo.`,
    );

    if (key.startsWith("daterange_")) {
      const fieldName = key.replace("daterange_", "");

      // Check all filter groups systematically for daterange filters
      const filterGroups = [
        { filters: accountFilters, setter: setAccountFilters, type: "account" },
        {
          filters: opportunityFilters,
          setter: setOpportunityFilters,
          type: "opportunity",
        },
        {
          filters: touchpointFilters,
          setter: setTouchpointFilters,
          type: "touchpoint",
        },
      ];

      for (const { filters, setter, type } of filterGroups) {
        const structuredFilters = extractStructuredFilters(filters);
        const newFilters = structuredFilters.filter(
          (filter) =>
            !(
              filter.field === fieldName &&
              (filter.op === "GTE" || filter.op === "LTE")
            ),
        );

        if (newFilters.length !== structuredFilters.length) {
          const newFilterGroup = rebuildFilterGroup(newFilters, filters);
          setter(newFilterGroup);
          console.log(
            `Cleared daterange filter "${fieldName}" from ${type} filters`,
          );
          return;
        }
      }

      // If no daterange filter found in any group, warn about unknown daterange key
      console.warn(`Daterange key "${key}" not found in any filter group`);
      return;
    }

    // Validate key format before splitting
    if (!key.includes("_")) {
      console.warn(
        `Invalid filter key format: ${key}. Expected format: "filterType_index"`,
      );
      return;
    }

    const [filterType, indexStr] = key.split("_");
    const index = parseInt(indexStr, 10);

    const config = getFiltersAndSetter(filterType as FilterEntityType);
    if (!config) {
      console.warn(`Unknown filter type: ${filterType}`);
      return;
    }

    const { filters, setter } = config;
    const structuredFilters = extractStructuredFilters(filters);
    if (index >= 0 && index < structuredFilters.length) {
      const newFilters = structuredFilters.filter((_, i) => i !== index);
      const newFilterGroup = rebuildFilterGroup(newFilters, filters);
      setter(newFilterGroup);
    } else {
      console.warn(`Invalid index: ${index}. No filter removed.`);
    }
  };
}
